import {
  AUTH_DEVICE_CLIENT_ID,
  AUTH_DEVICE_END_SESSION_EP,
  AUTH_DEVICE_GRANT,
  AUTH_DEVICE_TOKEN_EP,
  VITE_AUTH_DEVICE_AUTH_EP,
} from 'config';
import {
  createRequestOptions,
  objectToUrl,
} from 'utils/device-auth/auth-utils';
import {
  AuthRequestData,
  AuthStore,
  LoginData,
  PollingRequestData,
} from 'utils/device-auth/types';
import env from 'utils/env/env';
import { dispatchLeavePage } from 'utils/post-messages/dispatch-leave-page';
import { TimerManager } from 'utils/timer-manager/timer-manager';
import { StoreApi } from 'zustand';

export const loginInit = async (
  set: StoreApi<AuthStore>['setState'],
  get: StoreApi<AuthStore>['getState'],
  forceInit?: boolean
) => {
  if (
    (get().loginData ||
      get().status === 'polling' ||
      get().status === 'loading') &&
    !forceInit
  )
    return;

  // Reset loginData to re-initialize flow correctly
  set(() => ({
    loginData: null,
    token: '',
    expiresIn: 0,
    isAuthenticated: false,
  }));

  set(() => ({ status: 'loading' }));
  try {
    // No client_secret: `kiosk` is a public client. A browser cannot keep a
    // secret — it would have to be served in config/env.js, which is public —
    // and OpenIddict rejects a secret sent by a public client.
    const body: AuthRequestData = {
      client_id: AUTH_DEVICE_CLIENT_ID ?? '',
      scope: env.VITE_AUTH_DEVICE_SCOPE ?? '',
    };

    const response = await fetch(
      VITE_AUTH_DEVICE_AUTH_EP,
      createRequestOptions(objectToUrl(body))
    );

    if (response.ok) {
      const data: LoginData = await response.json();

      set(() => ({
        loginData: data,
      }));
    } else {
      get().actions.setError('Error.LoginInit');
    }
  } catch (error) {
    get().actions.setError('Error.LoginInit');
  }
};

/**
 * Fallback poll interval in seconds. RFC 8628 §3.5 tells clients to use 5 seconds when the
 * device authorization response omits `interval`, which identity's does.
 */
const DEFAULT_POLLING_INTERVAL_SECONDS = 5;

/** RFC 8628 §3.5: on `slow_down` the client must add 5 seconds to its poll interval. */
const SLOW_DOWN_INCREMENT_SECONDS = 5;

/**
 * How many polls in a row may fail before the flow is given up on. A single dropped request
 * must not end the login: the device code is still pending server-side, so the next poll would
 * have succeeded. Kiosks are on Wi-Fi, so the occasional failed request is normal.
 */
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export const loginPolling = async (
  set: StoreApi<AuthStore>['setState'],
  get: StoreApi<AuthStore>['getState']
) => {
  const loginData = get().loginData;
  const status = get().status;

  if (
    !loginData ||
    status === 'polling' ||
    status === 'error' ||
    status === 'done'
  )
    return;

  set(() => ({ status: 'polling' }));

  const body: PollingRequestData = {
    client_id: AUTH_DEVICE_CLIENT_ID ?? '',
    grant_type: AUTH_DEVICE_GRANT ?? '',
    device_code: loginData.device_code,
  };

  const requestOptions = createRequestOptions(objectToUrl(body));
  const pollingId = 'login-polling';

  // Accept the server's interval only when it is actually usable. Anything else — absent,
  // NaN, zero, negative — falls back to the RFC default instead of reaching setInterval and
  // turning the poll into a hot loop.
  const requestedInterval = loginData.interval;
  let intervalSeconds =
    typeof requestedInterval === 'number' &&
    Number.isFinite(requestedInterval) &&
    requestedInterval > 0
      ? requestedInterval
      : DEFAULT_POLLING_INTERVAL_SECONDS;

  let consecutiveFailures = 0;

  const polling = async () => {
    try {
      const response = await fetch(AUTH_DEVICE_TOKEN_EP, requestOptions);
      const data = await response.json();

      // The request reached the server and came back parseable, so whatever earlier
      // connection trouble there was is over.
      consecutiveFailures = 0;

      if (response.ok) {
        set(() => ({
          token: data.access_token,
          expiresIn: data.expires_in,
          isAuthenticated: true,
          status: 'done',
        }));
        TimerManager.stopTimer(pollingId, 'interval');
        get().actions.revokeTokenAfterExpiration();
      } else if (data.error === 'slow_down') {
        // Back off as the spec requires. startTimer clears the timer registered under the
        // same id first, so this replaces the current one rather than adding a second.
        intervalSeconds += SLOW_DOWN_INCREMENT_SECONDS;
        TimerManager.startTimer(
          pollingId,
          'interval',
          intervalSeconds,
          polling
        );
      } else if (data.error && data.error !== 'authorization_pending') {
        get().actions.setError('Error.LoginFlowExpired');
        dispatchLeavePage();
        TimerManager.stopTimer(pollingId, 'interval');
      }
    } catch (_err) {
      // A throw here means the request never produced a usable response — connection reset,
      // DNS, offline. The device code itself is untouched, so keep polling and only give up
      // once several attempts in a row have failed.
      consecutiveFailures += 1;

      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        get().actions.setError('Error.LoginInit');
        TimerManager.stopTimer(pollingId, 'interval');
      }
    }
  };

  TimerManager.startTimer(pollingId, 'interval', intervalSeconds, polling);
};

export const revokeToken = (
  set: StoreApi<AuthStore>['setState'],
  get: StoreApi<AuthStore>['getState']
) => {
  const expiresIn = get().expiresIn;
  const tokenExpirationId = 'token-expiration';

  const revokeToken = () => {
    set(() => ({
      loginData: null,
      token: '',
      expiresIn: 0,
      isAuthenticated: false,
      status: 'idle',
    }));
    window.location.assign('/');
  };

  TimerManager.startTimer(tokenExpirationId, 'timeout', expiresIn, revokeToken);
};

export const logout = async (
  set: StoreApi<AuthStore>['setState'],
  get: StoreApi<AuthStore>['getState']
) => {
  try {
    await fetch(AUTH_DEVICE_END_SESSION_EP, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${get().token}`,
      },
      credentials: 'include',
    });
  } catch (err) {
    //TODO better handling
    // eslint-disable-next-line no-console
    console.error(err);
  } finally {
    set(() => ({
      loginData: null,
      token: '',
      expiresIn: 0,
      isAuthenticated: false,
      status: 'idle',
    }));
    TimerManager.stopAllTimers();
  }
};
